import React, { useState, useEffect, useRef, useCallback } from 'react';
import { images } from '../../assets';

// =========================================================================
// CONFIGURACIÓN DE TIEMPO DEL STORY (ANTES / DESPUÉS)
// Modifica este valor (en milisegundos) para hacer la animación más rápida o lenta:
// 2500 = 2.5 segundos | 3000 = 3 segundos | 4000 = 4 segundos
// =========================================================================
const STORY_DURATION_MS = 2500;

const resultsData = [
  {
    id: 1,
    name: 'Ana B.',
    city: 'Huánuco',
    achievement: '',
    timeframe: '4 Meses de entrenamiento',
    beforeImg: images.antes1,
    afterImg: images.despues1,
    testimonial: 'El plan de ejercicios estructurado me ayudó a crear un hábito saludable sin abandonar a las pocas semanas.',
  },
  {
    id: 2,
    name: 'Lucía R.',
    city: 'Huánuco',
    achievement: '',
    timeframe: '6 Meses de entrenamiento',
    beforeImg: images.antes2,
    afterImg: images.despues2,
    testimonial: 'Buscaba perder peso y tonificar mis piernas. La constancia y guía profesional en Energy hicieron poder lograr mi objetivo y verme mejor.',
  },
  {
    id: 3,
    name: 'David S.',
    city: 'Huánuco',
    achievement: '',
    timeframe: '8 Meses de entrenamiento',
    beforeImg: images.antes3,
    afterImg: images.despues3,
    testimonial: 'Empecé desde cero sin saber usar las máquinas. Los instructores me guiaron con la técnica y la rutina adecuada, me siento bien como me veo',
  }
];

const ResultsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('antes'); // Inicia siempre en 'antes'
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const touchStartX = useRef(null);
  const touchEndX = useRef(null);

  const total = resultsData.length;

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % total);
    setCurrentPhase('antes');
    setProgress(0);
  }, [total]);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev === 0 ? total - 1 : prev - 1));
    setCurrentPhase('antes');
    setProgress(0);
  }, [total]);

  // Motor de animación fluida estilo Instagram Stories (Antes -> Después -> Siguiente persona)
  useEffect(() => {
    if (isPaused) return;

    const intervalTime = 30; // 30ms para animación continua y súper suave
    const step = (intervalTime / STORY_DURATION_MS) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (currentPhase === 'antes') {
            // Pasa de ANTES a DESPUÉS
            setCurrentPhase('despues');
            return 0;
          } else {
            // Al terminar DESPUÉS, avanza automáticamente a la siguiente persona
            nextSlide();
            return 0;
          }
        }
        return prev + step;
      });
    }, intervalTime);

    return () => clearInterval(timer);
  }, [isPaused, currentPhase, nextSlide]);

  // Soporte de gestos táctiles (Swipe)
  const handleTouchStart = (e) => {
    touchStartX.current = e.targetTouches[0].clientX;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;
    const distance = touchStartX.current - touchEndX.current;
    if (distance > 50) {
      nextSlide();
    } else if (distance < -50) {
      prevSlide();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <section
      className="relative w-full h-full flex flex-col justify-center items-center py-2 sm:py-3 md:py-4 px-4 md:px-8 bg-transparent overflow-hidden select-none border-none outline-none"
    >
      <div className="max-w-7xl mx-auto w-full relative z-10 flex flex-col justify-center items-center">

        {/* Encabezado limpio y directo adaptado al tono crema */}
        <div className="text-center max-w-3xl mx-auto mb-3 sm:mb-4 md:mb-5">
          <h2 className="anton-font text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-neutral-900 uppercase leading-tight tracking-normal mb-1.5 transition-colors duration-300">
            TRANSFORMACIONES EN <span className="text-blue-600">ENERGY GYM</span>
          </h2>

          <p className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed font-medium transition-colors duration-300">
            Resultados reales de personas de Huánuco que transformaron su físico y salud con constancia y guía profesional.
          </p>
        </div>

        {/* Carrusel Coverflow */}
        <div
          className="relative w-full flex justify-center items-center min-h-[380px] sm:min-h-[420px] md:min-h-[460px] max-h-[58vh] select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {resultsData.map((item, index) => {
            let offset = index - activeIndex;
            if (offset < -1 && total > 2) offset += total;
            if (offset > 1 && total > 2) offset -= total;

            const isCenter = offset === 0;
            const isLeft = offset === -1;
            const isRight = offset === 1;

            let positionClasses = 'opacity-0 pointer-events-none scale-75 z-0';
            if (isCenter) {
              positionClasses = 'opacity-100 z-30 scale-100 translate-x-0 shadow-[0_20px_60px_rgba(0,0,0,0.85)]';
            } else if (isLeft) {
              positionClasses = 'opacity-100 z-10 scale-[0.84] md:scale-[0.88] -translate-x-[60%] md:-translate-x-[72%] shadow-[0_12px_35px_rgba(0,0,0,0.5)] cursor-pointer';
            } else if (isRight) {
              positionClasses = 'opacity-100 z-10 scale-[0.84] md:scale-[0.88] translate-x-[60%] md:translate-x-[72%] shadow-[0_12px_35px_rgba(0,0,0,0.5)] cursor-pointer';
            }

            return (
              <div
                key={item.id}
                onClick={() => {
                  if (isLeft) prevSlide();
                  if (isRight) nextSlide();
                }}
                className={`absolute w-[270px] sm:w-[320px] md:w-[350px] h-[370px] sm:h-[410px] md:h-[450px] max-h-[56vh] rounded-2xl overflow-hidden bg-black border border-neutral-800 transition-all duration-500 ease-out flex flex-col ${positionClasses}`}
              >
                {/* Contenedor de la Imagen con Crossfade Suave (Base negra 100% sólida para bloquear el grid) */}
                <div className="relative flex-1 w-full h-full overflow-hidden bg-black">

                  {/* Foto ANTES */}
                  <img
                    src={item.beforeImg}
                    alt={`Antes - ${item.name}`}
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ease-in-out ${isCenter && currentPhase === 'antes' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                  />

                  {/* Foto DESPUÉS */}
                  <img
                    src={item.afterImg}
                    alt={`Después - ${item.name}`}
                    className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-700 ease-in-out ${!isCenter || currentPhase === 'despues' ? 'opacity-100' : 'opacity-0 pointer-events-none'
                      }`}
                  />

                  {/* Velo suave para tarjetas laterales (sutil atenuación para no tapar la foto y manteniendo el bloqueo del grid) */}
                  {!isCenter && (
                    <div className="absolute inset-0 bg-black/25 hover:bg-black/10 transition-colors duration-300 pointer-events-none z-10"></div>
                  )}

                  {/* Gradientes integrados en la imagen para legibilidad natural */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60 pointer-events-none"></div>

                  {/* Barras de progreso tipo Story (Solo en la tarjeta activa central) */}
                  {isCenter && (
                    <div className="absolute top-4 left-4 right-4 z-20">
                      {/* Líneas de tiempo continuas */}
                      <div className="grid grid-cols-2 gap-2 mb-2.5">
                        {/* Barra ANTES */}
                        <div className="h-1 bg-white/25 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-75"
                            style={{
                              width: currentPhase === 'antes' ? `${progress}%` : '100%',
                            }}
                          ></div>
                        </div>
                        {/* Barra DESPUÉS */}
                        <div className="h-1 bg-white/25 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 rounded-full transition-all duration-75"
                            style={{
                              width: currentPhase === 'despues' ? `${progress}%` : '0%',
                            }}
                          ></div>
                        </div>
                      </div>

                      {/* Selector de fase interactivo directo */}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPhase('antes');
                              setProgress(0);
                            }}
                            className={`font-semibold tracking-wider transition-colors cursor-pointer ${currentPhase === 'antes'
                              ? 'text-white border-b-2 border-blue-500 pb-0.5'
                              : 'text-gray-400 hover:text-gray-200'
                              }`}
                          >
                            ANTES
                          </button>
                          <span className="text-gray-600">/</span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentPhase('despues');
                              setProgress(0);
                            }}
                            className={`font-semibold tracking-wider transition-colors cursor-pointer ${currentPhase === 'despues'
                              ? 'text-white border-b-2 border-blue-500 pb-0.5'
                              : 'text-gray-400 hover:text-gray-200'
                              }`}
                          >
                            DESPUÉS
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Información y Testimonio integrado de forma natural sobre la foto */}
                  <div className="absolute bottom-0 left-0 right-0 p-5 z-20 text-left">
                    <div className="mb-2">
                      <div className="flex items-baseline justify-between gap-2">
                        <h3 className="text-white font-bold text-lg tracking-tight">
                          {item.name}
                        </h3>
                        <span className="text-blue-400 font-bold text-sm tracking-tight">
                          {item.achievement}
                        </span>
                      </div>
                      <p className="text-gray-400 text-xs mt-0.5">
                        {item.city} • {item.timeframe}
                      </p>
                    </div>

                    <p className="text-gray-200 text-sm leading-relaxed italic font-normal">
                      "{item.testimonial}"
                    </p>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Flecha Izquierda */}
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Ver resultado anterior"
            className="absolute left-1 sm:left-2 md:left-6 lg:left-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/85 hover:bg-blue-600 text-white border border-neutral-700 hover:border-blue-500 flex items-center justify-center transition-all duration-300 shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Flecha Derecha */}
          <button
            type="button"
            onClick={nextSlide}
            aria-label="Ver siguiente resultado"
            className="absolute right-1 sm:right-2 md:right-6 lg:right-8 top-1/2 -translate-y-1/2 z-40 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/85 hover:bg-blue-600 text-white border border-neutral-700 hover:border-blue-500 flex items-center justify-center transition-all duration-300 shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-500 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Indicadores de Puntos Inferiores */}
        <div className="flex justify-center items-center gap-2.5 mt-2.5 sm:mt-3">
          {resultsData.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => {
                setActiveIndex(index);
                setCurrentPhase('antes');
                setProgress(0);
              }}
              aria-label={`Ir a resultado ${index + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${index === activeIndex
                ? 'w-7 bg-blue-600'
                : 'w-2 bg-neutral-700 hover:bg-neutral-500'
                }`}
            />
          ))}
        </div>

      </div>
    </section>
  );
};

export default ResultsSection;
